/**
 * Override to add isLoading prop
 */

import { useNotifications } from '@/data/Content/Notifications';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { productFetcher } from '@/data/Content/_Product';
import {
	WL_NAME_REGEX,
	CreateEditData as _CreateEditData,
	PageData as _PageData,
	wishListCreator,
	wishListRemoverOrItemRemover,
	wishListUpdater,
	wishListsFetcher,
	wishListsMapper,
} from '@/data/Content/_Wishlists';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { DATA_KEY_WISH_LIST } from '@/data/constants/dataKey';
import {
	WISHLIST_PAGE_SIZE,
	WISHLIST_STATE,
	WL_NAME_REGEX as WL_NEW_NAME_REGEX,
} from '@/data/constants/wishlist';
import { TransactionErrorResponse } from '@/data/types/Basic';

import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getCurrencyParamFromContext } from '@/data/utils/getCurrencyParamFromContext';
import { invalidWishListName } from '@/data/utils/invalidWishListName';
import { mapProductsByPN } from '@/data/utils/mapProductData';
import { processError } from '@/data/utils/processError';
import type {
	WishlistWishlist,
	WishlistWishlistItem,
} from 'integration/generated/transactions/data-contracts';
import { keyBy } from 'lodash';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import { ChangeEvent, MouseEvent, useCallback, useMemo, useState } from 'react';
import { WISH_LIST_DEFAULT } from 'shared/custom/constants/commonConstants';
import useSWR from 'swr';
export { WL_NAME_REGEX, wishListRemoverOrItemRemover, wishListsFetcher, wishListsMapper };
export type PageData = _PageData;
export type CreateEditData = _CreateEditData;

const normalize = (s: string) => s.trim().toLocaleLowerCase();

export const useWishLists = () => {
	const routes = useLocalization('Routes');
	const router = useNextRouter();
	const { user } = useUser();
	const { query } = router;
	const params = useExtraRequestParameters();
	const { id } = query;
	const [pagination, setPagination] = useState<PageData>({
		pageNumber: 1,
		pageSize: WISHLIST_PAGE_SIZE,
	});
	const [creationData, setCreationData] = useState<CreateEditData>({ name: '' } as CreateEditData);
	const { settings } = useSettings();
	const { langId } = getClientSideCommon(settings, router);

	const { showSuccessMessage, notifyError } = useNotifications();
	const trackException = useTrackException();

	// get wishlists
	const {
		data,
		error,
		isLoading,
		mutate: mutateWishLists,
	} = useSWR(
		user?.isLoggedIn && settings?.storeId
			? [{ storeId: settings.storeId, pagination }, DATA_KEY_WISH_LIST]
			: null,
		async ([{ storeId, pagination }]) =>
			wishListsFetcher(true)(storeId, pagination, undefined, params),
		{ revalidateIfStale: true }
	);

	// tidy
	const { wishLists, totalPages, totalLists } = useMemo(
		() => wishListsMapper(data as WishlistWishlist, pagination),
		[data, pagination]
	);

	// index wishLists by their ids
	const wishListMap = useMemo(() => keyBy(wishLists, 'uniqueID'), [wishLists]);

	// get products inside each wishlist
	const partNumber = useMemo(
		() =>
			wishLists.flatMap((wishList) => wishList?.item?.map(({ partNumber }) => partNumber) ?? []),
		[wishLists]
	);

	const { data: products, error: productsError } = useSWR(
		settings?.storeId && partNumber.length
			? [
					{
						storeId: settings.storeId,
						partNumber,
						langId,
						...getCurrencyParamFromContext(user?.context),
					},
					DATA_KEY_WISH_LIST,
			  ]
			: null,
		async ([{ storeId, partNumber, currency, langId }]) =>
			productFetcher(true)({ storeId, partNumber, currency, langId }, params)
	);
	// index those products by their partNumbers
	const productMap = useMemo(() => mapProductsByPN(products), [products]);

	const localization = useLocalization('WishList');
	const success = useLocalization('success-message');

	/** @deprecated use `invalidWishListName` */
	const invalidName = useCallback(
		(name: string) => !name?.trim().length || !WL_NAME_REGEX.test(name),
		[]
	);

	type OnCreateResult = { error: boolean; wishListCreated?: WishlistWishlistItem };

	const validateWishListName = useCallback(
		(
			name: string,
			wishLists: WishlistWishlistItem[]
		): { valid: boolean; reason?: 'empty' | 'duplicate' } => {
			const normalized = normalize(name);
			if (normalized.length === 0) return { valid: false, reason: 'empty' };

			const exists = wishLists.some((w) => normalize(w.description ?? '') === normalized);
			if (exists) return { valid: false, reason: 'duplicate' };

			return { valid: true };
		},
		[]
	);

	const onCreateWishListByDefault = useCallback(async (): Promise<OnCreateResult> => {
		try {
			const wishListCreated = await wishListCreator(true)(
				settings?.storeId as string,
				{
					description: WISH_LIST_DEFAULT,
					registry: false,
					state: WISHLIST_STATE.ACTIVE,
				},
				undefined,
				params
			);

			const nextPageNumber = Math.ceil((totalLists + 1) / pagination.pageSize);

			if (nextPageNumber === pagination.pageNumber) {
				await mutateWishLists();
			} else {
				setPagination((prev) => ({
					...prev,
					pageNumber: nextPageNumber,
				}));
			}

			setCreationData({ name: '', error: false });

			return {
				error: false,
				wishListCreated: {
					uniqueID: wishListCreated.uniqueID,
					description: wishListCreated.descriptionName,
				} as WishlistWishlistItem,
			};
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
			trackException(e);
			return { error: true };
		}
	}, [
		settings?.storeId,
		params,
		totalLists,
		pagination.pageSize,
		pagination.pageNumber,
		mutateWishLists,
		notifyError,
		trackException,
	]);

	const onCreate = useCallback(
		async (_event: MouseEvent<HTMLButtonElement>): Promise<OnCreateResult> => {
			const validation = validateWishListName(creationData.name, wishLists);

			if (!validation.valid) {
				setCreationData((prev) => ({
					...prev,
					name: validation.reason === 'empty' ? '' : prev.name,
					error: true,
				}));
				return { error: true };
			}

			try {
				const normalizedName = creationData.name.trim();
				const wishListCreated = await wishListCreator(true)(
					settings?.storeId as string,
					{
						description: normalizedName,
						registry: false,
						state: WISHLIST_STATE.ACTIVE,
					},
					undefined,
					params
				);

				const nextPageNumber = Math.ceil((totalLists + 1) / pagination.pageSize);

				if (nextPageNumber === pagination.pageNumber) {
					await mutateWishLists();
				} else {
					setPagination((prev) => ({
						...prev,
						pageNumber: nextPageNumber,
					}));
				}

				setCreationData({ name: '', error: false });
				showSuccessMessage(success.CREATE_WISHLIST_SUCCESS.t([normalizedName]));

				return {
					error: false,
					wishListCreated: {
						uniqueID: wishListCreated.uniqueID,
						description: wishListCreated.descriptionName,
					} as WishlistWishlistItem,
				};
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
				trackException(e);
				return { error: true };
			}
		},
		[
			validateWishListName,
			creationData.name,
			wishLists,
			settings?.storeId,
			params,
			totalLists,
			pagination.pageSize,
			pagination.pageNumber,
			showSuccessMessage,
			success.CREATE_WISHLIST_SUCCESS,
			mutateWishLists,
			notifyError,
			trackException,
		]
	);

	/** @deprecated use `onNameV2` */
	const onName = useCallback(
		(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
			const newName = event.target.value;
			setCreationData((prev) => ({ ...prev, name: newName, error: invalidName(newName) }));
		},
		[invalidName]
	);

	const onNameV2 = useCallback((event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		const newName = event.target.value;
		if (WL_NEW_NAME_REGEX.test(newName)) {
			setCreationData((prev) => ({ ...prev, name: newName, error: invalidWishListName(newName) }));
		}
	}, []);

	const resetCreationData = useCallback(() => setCreationData({ name: '' } as CreateEditData), []);

	const onPage = useCallback((event: ChangeEvent<unknown>, page: number) => {
		setPagination((prev) => ({ ...prev, pageNumber: page }));
	}, []);

	const onDelete = useCallback(
		(wishList: WishlistWishlistItem, showNotification = true) =>
			async () => {
				try {
					await wishListRemoverOrItemRemover(true)(
						settings?.storeId as string,
						wishList.uniqueID as string,
						undefined,
						params
					);

					// go back a page if this page will no longer exist
					if (
						pagination.pageNumber > 1 &&
						pagination.pageNumber === totalPages &&
						wishLists.length === 1
					) {
						setPagination((prev) => ({ ...prev, pageNumber: pagination.pageNumber - 1 }));
					} else {
						mutateWishLists();
					}
					if (showNotification) {
						showSuccessMessage(success.DELETE_WISHLIST_SUCCESS.t([wishList.description as string]));
					}
				} catch (e) {
					notifyError(processError(e as TransactionErrorResponse));
					trackException(e);
				}
			},
		[
			mutateWishLists,
			notifyError,
			pagination,
			settings,
			showSuccessMessage,
			success,
			totalPages,
			wishLists,
			params,
			trackException,
		]
	);

	const onEditWishListTitle = useCallback((descriptionName: string) => {
		setCreationData({
			name: descriptionName ?? '',
			error: false,
		});
	}, []);

	/** @deprecated use `onViewV2` */
	const onView = useCallback(
		(wishList: WishlistWishlistItem) => () =>
			router.push({
				pathname: routes.WishLists.route.t(),
				query: { id: wishList.uniqueID },
			}),
		[router, routes.WishLists.route]
	);

	const onViewV2 = useCallback(
		(wishList: WishlistWishlistItem) => () =>
			router.push({
				pathname: routes.WishListDetails.route.t(),
				query: { id: wishList.uniqueID },
			}),
		[router, routes.WishListDetails.route]
	);

	/** @deprecated use `getCardActionsV2` */
	const getCardActions = useCallback(
		(wishList: WishlistWishlistItem) => {
			const cardAction = [];
			cardAction.push({
				text: localization.ViewList.t(),
				handleClick: onView(wishList),
			});
			if (wishList.state !== WISHLIST_STATE.DEFAULT) {
				cardAction.push({
					text: localization.Actions.DeleteList.t(),
					enableConfirmation: true,
					handleClick: onDelete(wishList),
				});
			}

			return cardAction;
		},
		[localization, onDelete, onView]
	);

	const getCardActionsV2 = useCallback(
		(wishList: WishlistWishlistItem) => {
			const cardAction = [];
			cardAction.push({
				text: localization.ViewList.t(),
				handleClick: onViewV2(wishList),
			});
			if (wishList.state !== WISHLIST_STATE.DEFAULT) {
				cardAction.push({
					text: localization.Actions.DeleteList.t(),
					enableConfirmation: true,
					handleClick: onDelete(wishList),
				});
			}

			return cardAction;
		},
		[localization, onDelete, onViewV2]
	);

	return {
		pagination,
		totalPages,
		data,
		error,
		productsError,
		isLoading,
		wishLists,
		onPage,
		getCardActions,
		productMap,
		invalidName,
		onName,
		onNameV2,
		onEditWishListTitle,
		creationData,
		onCreate,
		onCreateWishListByDefault,
		id,
		wishListMap,
		onDelete,
		mutateWishLists,
		getCardActionsV2,
		resetCreationData,
	};
};
